"use client";

import { useRef, useCallback } from "react";
import { Video as VideoUI } from "@repo/ui-parkmobile/Video";
import type { Video } from "@/generated/sb";
import { assetResolver } from "@/lib/storyblok/adaptors";
import { extractVideoConfig } from "@/lib/utils/wistia";
import { useWistiaId } from "@/hooks/useWistiaId";
import { WistiaPlayer } from "@wistia/wistia-player-react";

export interface VideoProps {
  blok: Video;
  className?: string;
  aspectRatio?: string;
  quality?: number;
  borderRadius?: string;
  imageSpace?: "fill" | "fit";
  playButtonImage?: {
    src: string;
    alt: string;
    width: number;
    height: number;
  };
  maxWidth?: number;
  maxHeight?: number;
  autoplay?: boolean;
  loop?: boolean;
  muted?: boolean;
}

export default function Video({
  blok,
  className,
  aspectRatio,
  quality,
  borderRadius,
  imageSpace,
  playButtonImage,
  maxWidth,
  maxHeight,
  autoplay = false,
  loop = false,
  muted = false,
}: VideoProps) {
  const wistiaId = useWistiaId(blok.url);
  const config = extractVideoConfig(blok.configuration);
  const playerColor = config?.playerColor?.replace(/^#/, "");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const playerRef = useRef<any>(null);

  const handleApiReady = useCallback(() => {
    if (playerRef.current && playerColor) {
      (playerRef.current as any).playerColor = playerColor;
    }
  }, [playerColor]);

  if (wistiaId) {
    return (
      <div
        className={className}
        style={{
          aspectRatio: aspectRatio || "3/2",
          borderRadius,
          width: "100%",
          overflow: "hidden",
        }}
      >
        <WistiaPlayer
          ref={playerRef}
          mediaId={wistiaId}
          onApiReady={handleApiReady}
          autoplay={config?.autoplay ?? autoplay}
          muted={config?.muted ?? muted}
          playerColor={playerColor}
          silentAutoplay={config?.silentAutoplay}
          resumable={config?.resumable}
          bigPlayButton={config?.bigPlayButton}
          volumeControl={config?.volumeControl}
          playBarControl={config?.playBarControl}
          qualityControl={config?.qualityControl}
          settingsControl={config?.settingsControl}
          playPauseControl={config?.playPauseControl}
          fullscreenControl={config?.fullscreenControl}
          playbackRateControl={config?.playbackRateControl}
          controlsVisibleOnLoad={config?.controlsVisibleOnLoad}
          endVideoBehavior={
            config?.endVideoBehavior as "reset" | "default" | "loop" | undefined
          }
          preload={config?.preload as "auto" | "metadata" | "none" | undefined}
          style={{
            width: "100%",
            height: "100%",
            borderRadius,
          }}
        />
      </div>
    );
  }

  const asset = assetResolver(blok.content);

  if (!asset || !asset.src) {
    return null;
  }

  // For video poster, we rely on preload="metadata" in the Video component
  // which will load the first frame automatically. This is more reliable
  // than trying to generate a poster URL which may not work for all sources.

  return (
    <VideoUI
      src={asset.src}
      alt={asset.alt}
      className={className}
      aspectRatio={aspectRatio}
      quality={quality}
      borderRadius={borderRadius}
      imageSpace={imageSpace}
      playButtonImage={playButtonImage}
      maxWidth={maxWidth}
      maxHeight={maxHeight}
      autoplay={autoplay}
      loop={loop}
      muted={muted}
      controls={true}
      playsInline={true}
    />
  );
}
